/**
 * PageTemplate - Unified page layout for list/table views
 *
 * Based on FournisseursView pattern for consistency across:
 * - Fournisseurs, Reports, Archive, and similar views
 *
 * Provides:
 * - Header card with icon, title, subtitle
 * - Action buttons area
 * - Optional view toggle
 * - Search and filters section
 * - Tabs support
 * - Content area
 */

import React from 'react';
import { Search, RefreshCw, X, Loader2, AlertTriangle } from 'lucide-react';
import { ViewModeToggle } from './ViewToggle';
import { useTranslation } from 'react-i18next';

/**
 * PageTemplate component
 *
 * @param {Object} props
 * @param {React.ElementType} props.icon - Icon component for header
 * @param {string} props.iconColor - Tailwind color class for icon (e.g., 'text-amber-600')
 * @param {string} props.title - Page title
 * @param {string} props.subtitle - Page subtitle/description
 * @param {React.ReactNode} props.actions - Action buttons (right side of header)
 * @param {boolean} props.loading - Show loading spinner
 * @param {function} props.onRefresh - Refresh callback
 * @param {string} props.searchPlaceholder - Search input placeholder
 * @param {string} props.searchValue - Current search value
 * @param {function} props.onSearchChange - Search change callback
 * @param {React.ReactNode} props.filters - Additional filter components
 * @param {Array} props.tabs - Tab definitions [{id, label, icon}]
 * @param {string} props.activeTab - Current active tab id
 * @param {function} props.onTabChange - Tab change callback
 * @param {string} props.viewMode - Current view mode ('grouped', 'list', etc.)
 * @param {function} props.onViewModeChange - View mode change callback
 * @param {Array} props.viewModeOptions - Available view modes
 * @param {string} props.viewModeStorageKey - localStorage key for view mode
 * @param {string} props.error - Error message to display
 * @param {function} props.onRetry - Retry callback for error state
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.accentColor - Accent color for buttons (e.g., 'amber', 'blue')
 */
export function PageTemplate({
  icon: Icon,
  iconColor = 'text-blue-600',
  title,
  subtitle,
  actions,
  loading = false,
  onRefresh,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  filters,
  tabs,
  activeTab,
  onTabChange,
  viewMode,
  onViewModeChange,
  viewModeOptions,
  viewModeStorageKey,
  error,
  onRetry,
  children,
  accentColor = 'blue'
}) {
  const { t } = useTranslation();
  const accentColors = {
    blue: {
      button: 'bg-blue-500 hover:bg-blue-600',
      focus: 'focus:ring-blue-500 focus:border-blue-500',
      tab: 'bg-blue-600 text-white',
    },
    amber: {
      button: 'bg-amber-500 hover:bg-amber-600',
      focus: 'focus:ring-amber-500 focus:border-amber-500',
      tab: 'bg-amber-600 text-white',
    },
    emerald: {
      button: 'bg-emerald-500 hover:bg-emerald-600',
      focus: 'focus:ring-emerald-500 focus:border-emerald-500',
      tab: 'bg-emerald-600 text-white',
    },
    slate: {
      button: 'bg-slate-700 hover:bg-slate-800',
      focus: 'focus:ring-slate-500 focus:border-slate-500',
      tab: 'bg-slate-700 text-white',
    },
  };

  const colors = accentColors[accentColor] || accentColors.blue;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          {/* Title Section */}
          <div className="flex items-center gap-3">
            {Icon && <Icon className={`w-8 h-8 ${iconColor}`} />}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
              {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
            </div>
          </div>

          {/* Actions Section */}
          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            {viewMode && onViewModeChange && viewModeOptions && (
              <ViewModeToggle
                value={viewMode}
                onChange={onViewModeChange}
                options={viewModeOptions}
                storageKey={viewModeStorageKey}
              />
            )}

            {/* Refresh Button */}
            {onRefresh && (
              <button
                onClick={onRefresh}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                title={t('common.actions.refresh')}
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}

            {/* Custom Actions */}
            {actions}
          </div>
        </div>

        {/* Search and Filters Row */}
        {(onSearchChange || filters) && (
          <div className="flex flex-wrap items-center gap-3">
            {/* Search Input */}
            {onSearchChange && (
              <div className="relative flex-1 min-w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={searchPlaceholder ?? t('common.search.placeholder')}
                  value={searchValue || ''}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 ${colors.focus}`}
                />
                {searchValue && (
                  <button
                    onClick={() => onSearchChange('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}

            {/* Additional Filters */}
            {filters}
          </div>
        )}

        {/* Tabs (inside header card) */}
        {tabs && tabs.length > 0 && (
          <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
            {tabs.map(tab => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => onTabChange?.(tab.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all ${
                    isActive
                      ? colors.tab + ' shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {TabIcon && <TabIcon className="w-5 h-5" />}
                  {tab.label}
                  {tab.badge !== undefined && (
                    <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                      isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="flex flex-col items-center justify-center py-12 text-red-500">
            <AlertTriangle className="w-12 h-12 mb-2" />
            <p>{error}</p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                {t('common.actions.retry')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Loading State (full page) */}
      {loading && !children && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="flex items-center justify-center py-12">
            <Loader2 className={`w-8 h-8 ${iconColor} animate-spin`} />
          </div>
        </div>
      )}

      {/* Content */}
      {!error && children}
    </div>
  );
}

/**
 * ContentCard - Standard content card wrapper
 */
export function ContentCard({ children, className = '', noPadding = false }) {
  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
      {noPadding ? children : <div className="p-6">{children}</div>}
    </div>
  );
}

/**
 * DataTable - Standard table wrapper with header and footer
 */
export function DataTable({
  columns,
  data,
  loading,
  emptyIcon: EmptyIcon,
  emptyMessage,
  onResetFilters,
  hasFilters = false,
  footer,
  renderRow,
}) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        {EmptyIcon && <EmptyIcon className="w-12 h-12 mb-2 text-gray-300" />}
        <p>{emptyMessage ?? t('common.states.emptyFound')}</p>
        {hasFilters && onResetFilters && (
          <button
            onClick={onResetFilters}
            className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            {t('common.actions.resetFilters')}
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  className={`px-4 py-3 text-xs font-semibold text-gray-600 uppercase ${
                    col.align === 'center' ? 'text-center' :
                    col.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.map((item, idx) => renderRow(item, idx))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {footer || (
        <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-600">
          {t('common.pagination.itemsFound', { count: data.length })}
        </div>
      )}
    </>
  );
}

export default PageTemplate;
